// Reusable organization picker modal — department tree + members, single/multi
// select. Used anywhere people need to be chosen (approval line/refs, mail, …).
import { useMemo, useState } from "react";
import { I } from "../Icon";
import { useOrg, membersOf, type DeptNode } from "./useOrg";

const UNASSIGNED = -1;   // 부서 미지정 가상 노드

export type OrgPickUser = { id: number; name: string; email?: string | null; department_name?: string | null; position_name?: string | null };

export function OrgPicker({ onPick, onClose, multi = true, excludeIds = [], title = "조직도에서 선택" }: {
  onPick: (users: OrgPickUser[]) => void;
  onClose: () => void;
  multi?: boolean;
  excludeIds?: number[];
  title?: string;
}) {
  const { tree, users, loading } = useOrg();
  const [deptId, setDeptId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<Record<number, OrgPickUser>>({});
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const excluded = new Set(excludeIds);

  // 부서 트리에 존재하는 부서 id 집합 (미지정 판별용)
  const deptIds = useMemo(() => {
    const s = new Set<number>();
    const walk = (n: DeptNode) => { s.add(n.id); n.children.forEach(walk); };
    tree.forEach(walk);
    return s;
  }, [tree]);
  // 부서별 직속 인원수
  const directCount = useMemo(() => {
    const m: Record<number, number> = {};
    for (const u of users) { const d = (u as { department_id?: number | null }).department_id; if (d != null) m[d] = (m[d] || 0) + 1; }
    return m;
  }, [users]);
  const subtreeCount = (n: DeptNode): number =>
    (directCount[n.id] || 0) + n.children.reduce((s, c) => s + subtreeCount(c), 0);
  // 부서가 없거나(또는 트리에 없는 부서) 인원 — 게스트/미배정 사용자 등
  const unassigned = useMemo(
    () => users.filter((u) => { const d = (u as { department_id?: number | null }).department_id; return d == null || !deptIds.has(d); }),
    [users, deptIds],
  );

  const members = (q.trim()
    ? users.filter((u) => u.name.toLowerCase().includes(q.trim().toLowerCase()))
    : deptId === UNASSIGNED ? unassigned
    : (deptId != null ? membersOf(users, deptId) : []))
    .filter((u) => !excluded.has(u.id));

  const pickOne = (u: OrgPickUser) => {
    if (multi) { setSel((s) => { const n = { ...s }; if (n[u.id]) delete n[u.id]; else n[u.id] = u; return n; }); }
    else { onPick([u]); onClose(); }
  };
  const chosen = Object.values(sel);

  const renderNode = (n: DeptNode, depth: number) => (
    <div key={n.id}>
      <div onClick={() => setDeptId(n.id)}
        style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", paddingLeft: 8 + depth * 14, cursor: "pointer", borderRadius: 6, fontSize: 13, background: deptId === n.id ? "rgba(99,102,241,0.12)" : "transparent", color: deptId === n.id ? "var(--p-600,#4f46e5)" : "var(--text-2)" }}>
        {n.children.length > 0
          ? <span onClick={(e) => { e.stopPropagation(); setCollapsed((x) => ({ ...x, [n.id]: !x[n.id] })); }} style={{ fontSize: 9, width: 12, color: "var(--text-3)" }}>{collapsed[n.id] ? "▶" : "▼"}</span>
          : <span style={{ width: 12 }} />}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📁 {n.name}</span>
        <span style={{ fontSize: 11, color: "var(--text-3)", fontVariantNumeric: "tabular-nums" }}>{subtreeCount(n)}</span>
      </div>
      {!collapsed[n.id] && n.children.map((c) => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: "min(720px, 95vw)", height: "70vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, flex: 1 }}>{title}</h3>
          <input className="t-input" style={{ width: 200, fontSize: 13 }} placeholder="이름 검색" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="t-iconbtn" onClick={onClose}><I.X size={16} /></button>
        </div>
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          <div style={{ width: 240, borderRight: "1px solid var(--border)", overflowY: "auto", padding: 6 }}>
            {loading && <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>불러오는 중…</div>}
            {tree.map((n) => renderNode(n, 0))}
            {unassigned.length > 0 && (
              <div onClick={() => setDeptId(UNASSIGNED)}
                style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", marginTop: 4, borderTop: "1px solid var(--border)", cursor: "pointer", borderRadius: 6, fontSize: 13, background: deptId === UNASSIGNED ? "rgba(99,102,241,0.12)" : "transparent", color: deptId === UNASSIGNED ? "var(--p-600,#4f46e5)" : "var(--text-2)" }}>
                <span style={{ width: 12 }} />
                <span style={{ flex: 1 }}>🗂️ 부서 미지정</span>
                <span style={{ fontSize: 11, color: "var(--text-3)", fontVariantNumeric: "tabular-nums" }}>{unassigned.length}</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
            {members.length === 0 && <div style={{ padding: 16, fontSize: 13, color: "var(--text-3)" }}>{q.trim() ? "검색 결과 없음" : "왼쪽에서 부서를 선택하세요"}</div>}
            {members.map((u) => (
              <div key={u.id} onClick={() => pickOne(u)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, cursor: "pointer", background: sel[u.id] ? "rgba(99,102,241,0.10)" : "transparent" }}>
                {multi && <span style={{ width: 16, height: 16, borderRadius: 4, border: "1.5px solid var(--border-strong)", display: "flex", alignItems: "center", justifyContent: "center", background: sel[u.id] ? "var(--p-600,#4f46e5)" : "transparent" }}>{sel[u.id] && <I.Check size={12} style={{ color: "#fff" }} />}</span>}
                <span style={{ flex: 1, fontSize: 13 }}>{u.name}</span>
                <span style={{ fontSize: 11, color: "var(--text-3)" }}>{u.position_name || ""}{u.department_name ? ` · ${u.department_name}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
        {multi && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)", flex: 1 }}>{chosen.length}명 선택</span>
            <button className="t-btn" onClick={onClose}>취소</button>
            <button className="t-btn t-btn-primary" onClick={() => { onPick(chosen); onClose(); }} disabled={!chosen.length}>추가</button>
          </div>
        )}
      </div>
    </div>
  );
}
